import { z } from 'zod'

/**
 * Voice layer (board card loap): the browser-facing shapes shared by the
 * Worker's provider registry (`worker/voice/registry.ts`) and the SPA's
 * dictation transports (`web/lib/dictation/*`). Which provider is live is
 * decided by env (`VOICE_TTS_PROVIDER`, `VOICE_STT_PROVIDER`) — Soniox by
 * default, ElevenLabs behind the same protocol.
 */

export const VOICE_PROVIDER_IDS = ['soniox', 'elevenlabs'] as const
export const voiceProviderIdSchema = z.enum(VOICE_PROVIDER_IDS)
export type VoiceProviderId = z.infer<typeof voiceProviderIdSchema>

/** Dictation stops by itself past this — a chat message, not a memo. */
export const MAX_DICTATION_SECONDS = 300

/**
 * Soniox's structured `context` (docs: stt/concepts/context), the words a
 * session should expect: built only by `worker/voice/context.ts`, sent once in
 * the realtime start message / the async create body. Soniox refuses the whole
 * session above 8 000 tokens (≈ 10 000 chars), so the builder keeps the
 * serialized object under `STT_CONTEXT_MAX_CHARS`.
 */
export const STT_CONTEXT_MAX_CHARS = 6000
/**
 * A meeting's live stream sends its context again on every open, and Soniox bills
 * it as text tokens (≈ 0.8 per char, USD 4/M): ≈ 0.5 credits per open at this cap,
 * where the full cap would be ≈ 1.9, more than six billed stream-minutes.
 */
export const STT_CONTEXT_LIVE_MAX_CHARS = 1500
export const sttContextSchema = z.object({
  general: z.array(z.object({ key: z.string().min(1), value: z.string().min(1) })).optional(),
  text: z.string().min(1).optional(),
  terms: z.array(z.string().min(1)).optional(),
})
export type SttContext = z.infer<typeof sttContextSchema>

/**
 * What `POST /voice/stt/session` hands the browser: everything one dictation
 * session needs to open its own socket straight to the provider, and never
 * the long-lived API key. Soniox mints a single-use temporary key (60 s to
 * connect); ElevenLabs a single-use realtime token.
 */
export const sttRealtimeSessionSchema = z.discriminatedUnion('provider', [
  z.object({
    provider: z.literal('soniox'),
    /** `wss://stt-rt[.<region>].soniox.com/transcribe-websocket` */
    url: z.string().url(),
    /** Temporary, single-use `snx_temp_` key — sent as WebSocket protocols `['soniox-api-key', key]`. */
    apiKey: z.string().min(1),
    model: z.string().min(1),
    languageHints: z.array(z.string().min(1)),
    /** ISO time the temporary key stops opening sessions. */
    expiresAt: z.string(),
    maxSeconds: z.number().int().positive(),
    /** Project vocabulary for the start message; absent when the project has none. */
    context: sttContextSchema.optional(),
  }),
  z.object({
    provider: z.literal('elevenlabs'),
    token: z.string().min(1),
    model: z.string().min(1),
    languageCode: z.string().min(1),
    maxSeconds: z.number().int().positive(),
  }),
])
export type SttRealtimeSession = z.infer<typeof sttRealtimeSessionSchema>

/**
 * Uploads that go through async speech-to-text instead of Firecrawl
 * (`worker/pipelines/document-pipeline.ts` convert step). Video containers
 * are transcribed for their audio track.
 */
export const TRANSCRIBABLE_EXTENSIONS: ReadonlySet<string> = new Set([
  '.mp3',
  '.m4a',
  '.wav',
  '.ogg',
  '.oga',
  '.flac',
  '.aac',
  '.amr',
  '.aiff',
  '.webm',
  '.mp4',
])

export function isTranscribableExtension(ext: string): boolean {
  return TRANSCRIBABLE_EXTENSIONS.has(ext.toLowerCase())
}
